/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { TimeSeriesConnect } from './TimeSeriesConnect';
import { timeSeriesActions } from '../../store/reducer';
import type { TimeSeriesPreset } from '../../store/types';
import { getUserAddedEDRServices } from '../../utils/localStorage';

interface Props {
  panelId: string;
  timeSeriesPreset: TimeSeriesPreset;
  hideLocationMarkers?: boolean;
}

export const TimeSeriesConnectWrapper: React.FC<Props> = ({
  panelId,
  timeSeriesPreset,
  hideLocationMarkers,
}) => {
  const dispatch = useDispatch();

  // The preset should only be set once the component is mounted or when it has changed.
  const timeSeriesPresetRef = React.useRef<TimeSeriesPreset | undefined>(
    undefined,
  );

  // Load services from localstorage before timeseriespresets are loaded as after that preset registering action can change ids accordingly
  React.useEffect(() => {
    const localStorageServices = getUserAddedEDRServices();
    dispatch(
      timeSeriesActions.loadUserAddedTimeSeriesServices(localStorageServices),
    );
  }, [dispatch, panelId, timeSeriesPreset, timeSeriesPresetRef]);

  React.useEffect(() => {
    // Check if the preset has changed
    if (!isEqual(timeSeriesPreset, timeSeriesPresetRef.current)) {
      timeSeriesPresetRef.current = timeSeriesPreset;
      timeSeriesPreset &&
        dispatch(
          timeSeriesActions.registerTimeSeriesPreset({
            ...timeSeriesPreset,
            panelId,
          }),
        );

      hideLocationMarkers !== undefined &&
        dispatch(
          timeSeriesActions.toggleLocationMarkers({
            panelId,
            shouldHideLocationMarkers: hideLocationMarkers,
          }),
        );
    }
  }, [
    dispatch,
    timeSeriesPreset,
    timeSeriesPresetRef,
    hideLocationMarkers,
    panelId,
  ]);

  return <TimeSeriesConnect panelId={panelId} />;
};
